import { useState, useRef, useEffect, useCallback } from 'react';
import { COLOR, FONT, RADIUS, SHADOW } from '../utils/theme';
import { Icon } from '../utils/icons';

export default function TripOverflowMenu({ onEditTrip, onExportPDF, onExportWord }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, close]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, close]);

  const items = [
    { key: 'edit', icon: 'edit', label: 'Edit Trip', onClick: onEditTrip },
    { key: 'divider' },
    { key: 'pdf', icon: 'download', label: 'Export PDF', onClick: onExportPDF },
    { key: 'docx', icon: 'fileDown', label: 'Export Word', onClick: onExportWord },
  ];

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        data-testid="trip-overflow-button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: 36,
          height: 36,
          borderRadius: RADIUS.sm,
          background: open ? COLOR.buttonSecondary : 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = COLOR.buttonSecondary; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        <Icon name="overflow" size={18} color={COLOR.inkMid} />
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 180,
            background: COLOR.white,
            border: `1px solid ${COLOR.borderWarm}`,
            borderRadius: RADIUS.md,
            boxShadow: SHADOW.card,
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          {items.map((item) => {
            if (item.key === 'divider') {
              return (
                <div
                  key="divider"
                  style={{
                    height: 1,
                    background: COLOR.borderWarm,
                    margin: '4px 0',
                  }}
                />
              );
            }
            return (
              <button
                key={item.key}
                data-testid={`overflow-${item.key}`}
                onClick={() => {
                  close();
                  item.onClick?.();
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 16px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: FONT.serif,
                  fontSize: 14,
                  color: COLOR.inkDark,
                  textAlign: 'left',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = COLOR.cream; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon name={item.icon} size={16} color={COLOR.inkMid} />
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
